import type { Dispatch, SetStateAction } from 'react';
import { streetsMatchForSchool } from './teacher-prefill';

export type StudentPrefill = {
	personalTitle?: string;
	firstName?: string;
	secondName?: string;
	lastName?: string;
	parentName?: string;
	phone?: string;
	city?: string;
	street?: string;
	zipCode?: string;
	building?: string;
	apartment?: string;
	districtName?: string;
	class?: number | null;
	specialCertificate?: boolean;
	isStudentUnderage?: boolean;
	schoolRspo?: number | null;
	schoolName?: string;
	schoolCity?: string;
	specializationName?: string;
	/** Ukryty transfer z poprzedniej edycji — brak pola w formularzu rejestracji. */
	birthday?: string;
};

type DistrictRow = { id: number; name: string };

type SchoolRow = {
	id: number;
	rspo: number;
	name: string;
	city: string;
	street?: string;
	building?: string;
	districtID?: number | string;
	district_id?: number;
};

type SpecRow = { id: number; name: string };

type FormInfoShape = {
	personalTitle: string;
	parentName: string;
	parentEmail: string;
	firstName: string;
	secondName: string;
	lastName: string;
	class: number | string;
	specializationId: string;
	email: string;
	phone: string;
	password: string;
	passwordConfirm: string;
	districtId: string;
	teachersIds: any[];
	scientificTeacherIds?: number[];
	schoolTeacherId?: number | null;
	street: string;
	building: string;
	apartment: string;
	zipCode: string;
	city: string;
	schoolId: string;
	specialCertificate: boolean;
	isStudentUnderage: boolean;
	/** Ukryte — z poprzedniej edycji / nie na formularzu. */
	birthday?: string;
};

type HelperInputShape = {
	district: string;
	teachers: string[];
	scientificTeachers: string[];
	schoolTeacher: string;
	specialization: string;
	school: string;
	rodo: string;
	consent: string;
};

export type StudentPrefillApplyResult = {
	appliedForm: Partial<FormInfoShape>;
	appliedHelperDistrict: string;
	appliedHelperSchool: string;
	appliedHelperSpecialization: string;
	hasSecondName: boolean;
	schoolMatched: boolean;
};

const norm = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

function schoolDisplayLabel(school: SchoolRow): string {
	return `${school.city.toUpperCase()}, ${school.name.toUpperCase()}`;
}

function matchSchoolFromStudentPrefill(
	schools: SchoolRow[] | undefined,
	prefill: StudentPrefill,
): SchoolRow | undefined {
	if (!schools?.length) {
		return undefined;
	}
	const rspo = Number(prefill.schoolRspo || 0);
	if (rspo > 0) {
		const byRspo = schools.find((s) => Number(s.rspo) === rspo);
		if (byRspo) {
			return byRspo;
		}
	}
	const city = norm(prefill.schoolCity || prefill.city || '');
	const name = norm(prefill.schoolName || '');
	if (city && name) {
		const byName = schools.find((s) => norm(s.city) === city && norm(s.name) === name);
		if (byName) {
			return byName;
		}
	}
	const building = norm(prefill.building || '');
	if (city && building) {
		return schools.find(
			(s) =>
				norm(s.city) === city &&
				norm(s.building || '') === building &&
				streetsMatchForSchool(prefill.street || '', s.street || ''),
		);
	}
	return undefined;
}

/**
 * Prefill wracającego ucznia — szkoła/specjalizacja/klasa+1 z poprzedniej edycji;
 * nauczyciele i zgody pozostają do uzupełnienia.
 */
export function applyStudentPrefill<T extends FormInfoShape>(
	prefill: StudentPrefill,
	districts: DistrictRow[] | undefined,
	schools: SchoolRow[] | undefined,
	specializations: SpecRow[] | undefined,
	setFormInfo: Dispatch<SetStateAction<T>>,
	setHelperInputInfo: Dispatch<SetStateAction<HelperInputShape>>,
	setIsSecondName: (value: boolean) => void,
	forceUnderage: boolean,
): StudentPrefillApplyResult {
	const empty: StudentPrefillApplyResult = {
		appliedForm: {},
		appliedHelperDistrict: '',
		appliedHelperSchool: '',
		appliedHelperSpecialization: '',
		hasSecondName: false,
		schoolMatched: false,
	};
	if (!prefill || !districts) {
		return empty;
	}

	const matchedSchool = matchSchoolFromStudentPrefill(schools, prefill);
	let district = prefill.districtName ? districts.find((d) => d.name === prefill.districtName) : undefined;
	const schoolDistrictId = matchedSchool?.districtID ?? matchedSchool?.district_id;
	if (schoolDistrictId) {
		const fromSchool = districts.find((d) => d.id === Number(schoolDistrictId));
		if (fromSchool) {
			district = fromSchool;
		}
	}

	const specName = (prefill.specializationName || '').trim();
	const matchedSpec =
		specName && specializations?.length ? specializations.find((s) => norm(s.name) === norm(specName)) : undefined;

	const hasSecondName = !!(prefill.secondName && prefill.secondName.trim() !== '');
	const underage = forceUnderage || !!prefill.isStudentUnderage;
	const schoolLabel = matchedSchool ? schoolDisplayLabel(matchedSchool) : '';

	const appliedForm: Partial<FormInfoShape> = {
		personalTitle: prefill.personalTitle || '',
		firstName: prefill.firstName || '',
		secondName: prefill.secondName || '',
		lastName: prefill.lastName || '',
		parentName: underage ? prefill.parentName || '' : '',
		// Osobnego emaila rodzica nie przenosimy — kontakt = pole E-mail na formularzu.
		parentEmail: '',
		phone: prefill.phone || '',
		city: prefill.city || '',
		street: prefill.street || '',
		zipCode: prefill.zipCode || '',
		building: prefill.building || '',
		apartment: prefill.apartment || '',
		districtId: district ? String(district.id) : '',
		class: prefill.class != null ? String(prefill.class) : '',
		specialCertificate: !!prefill.specialCertificate,
		isStudentUnderage: underage,
		schoolId: matchedSchool ? String(matchedSchool.id) : '',
		specializationId: matchedSpec ? String(matchedSpec.id) : '',
		teachersIds: [],
		birthday: prefill.birthday || '',
	};

	setFormInfo((prev) => ({
		...prev,
		...appliedForm,
		email: prev.email,
		password: prev.password,
		passwordConfirm: prev.passwordConfirm,
	}));

	if (hasSecondName) {
		setIsSecondName(true);
	} else {
		setIsSecondName(false);
	}

	setHelperInputInfo((prev) => ({
		...prev,
		district: district?.name || prev.district,
		school: schoolLabel,
		specialization: matchedSpec?.name || prev.specialization,
		teachers: [],
		scientificTeachers: [],
		schoolTeacher: '',
	}));

	return {
		appliedForm,
		appliedHelperDistrict: district?.name || '',
		appliedHelperSchool: schoolLabel,
		appliedHelperSpecialization: matchedSpec?.name || '',
		hasSecondName,
		schoolMatched: !!matchedSchool,
	};
}
